import type { CSSProperties } from "react"

import { contrastForeground } from "@workspace/theming"

/**
 * Local token overrides for the `showcase` layout's DARK bands (utility bar,
 * masthead, banner caption strip, footer).
 *
 * The reference takeaway site runs near-black bands around its coloured nav
 * in BOTH colour schemes. Re-pointing the neutral surface tokens on the band's
 * own element lets the shared header controls render correctly on it with no
 * per-control styling — while `--primary` is left untouched, so tenant hue
 * still reaches pills and active states. A `.dark` class would not do:
 * globals.css's `.dark` block also resets `--primary` to a neutral and would
 * wipe the tenant colour inside the band.
 *
 * Portaled content (popovers, sheets, dialogs) mounts outside the band and so
 * keeps the page tokens — exactly right for overlays.
 *
 * `textShadow: none` stops the layout's on-photo halo (light in light mode)
 * from inheriting into bands that sit inside `<main>` — they're solid, and a
 * white halo would blur their light text.
 */
export const DARK_BAND_STYLE = {
  colorScheme: "dark",
  textShadow: "none",
  "--background": "oklch(0.145 0 0)",
  "--foreground": "oklch(0.985 0 0)",
  "--muted": "oklch(0.269 0 0)",
  "--muted-foreground": "oklch(0.78 0 0)",
  "--accent": "oklch(0.269 0 0)",
  "--accent-foreground": "oklch(0.985 0 0)",
  "--secondary": "oklch(0.269 0 0)",
  "--secondary-foreground": "oklch(0.985 0 0)",
  "--border": "oklch(1 0 0 / 12%)",
  "--input": "oklch(1 0 0 / 18%)",
} as CSSProperties

/**
 * Puts a band's colours under band-local names. Goes on the OUTER element of
 * a band; {@link BAND_STYLE} / {@link PRIMARY_BAND_STYLE} go on an element
 * INSIDE it. Two elements because the inner tokens are built from these, and
 * `PRIMARY_BAND_STYLE` swaps `--primary` with `--primary-foreground`: a custom
 * property can't read the values it is replacing on the same element (that's
 * a cycle, and the browser drops every var in it) — it has to read them off
 * its parent.
 *
 * `foreground` defaults to a contrast-derived colour (white text on a dark
 * band, near-black on a light one).
 */
export function bandCaptureStyle(
  background: string,
  foreground?: string | null
): CSSProperties {
  return {
    "--band": background,
    "--band-foreground": foreground ?? contrastForeground(background),
  } as CSSProperties
}

/** {@link bandCaptureStyle} for a band in the tenant primary, with its own foreground. */
export const PRIMARY_BAND_CAPTURE_STYLE = {
  "--band": "var(--primary)",
  "--band-foreground": "var(--primary-foreground)",
} as CSSProperties

/**
 * Token overrides for content on a coloured band (paint the band with
 * `bg-(--band)` under {@link bandCaptureStyle}): the neutrals become shades of
 * the band, so the shared controls render on it with no per-control
 * overrides — the order-type toggle's track (`bg-muted`) reads a tint lighter
 * than the band, its active pill (`bg-background`) a shade darker. Every shade
 * is derived from the band's own pair, so it stays legible whatever the hue.
 * `--primary` is left alone: the tenant colour still reaches pills.
 */
export const BAND_STYLE = {
  textShadow: "none",
  "--background": "oklch(from var(--band) calc(l - 0.1) c h)",
  "--foreground": "var(--band-foreground)",
  "--muted": "color-mix(in oklab, var(--band-foreground) 18%, var(--band))",
  "--muted-foreground":
    "color-mix(in oklab, var(--band-foreground) 85%, var(--band))",
  "--accent": "color-mix(in oklab, var(--band-foreground) 18%, var(--band))",
  "--accent-foreground": "var(--band-foreground)",
  "--secondary": "color-mix(in oklab, var(--band-foreground) 18%, var(--band))",
  "--secondary-foreground": "var(--band-foreground)",
  "--border": "color-mix(in oklab, var(--band-foreground) 30%, transparent)",
  "--input": "color-mix(in oklab, var(--band-foreground) 30%, transparent)",
  "--ring": "var(--band-foreground)",
} as CSSProperties

/**
 * {@link BAND_STYLE} for a band painted in the tenant primary: also inverts the
 * brand pair, so anything the shared controls paint `bg-primary` (the basket
 * pill, the active language) turns into a `--primary-foreground` pill with
 * primary text instead of vanishing into the band.
 */
export const PRIMARY_BAND_STYLE = {
  ...BAND_STYLE,
  "--primary": "var(--band-foreground)",
  "--primary-foreground": "var(--band)",
} as CSSProperties

/**
 * Token overrides for the reference site's white info panels ("Ways to pay"):
 * a light card with dark text in BOTH colour schemes, the mirror image of
 * {@link DARK_BAND_STYLE}. `--primary` is left alone for the panel's header
 * strip and border.
 */
export const LIGHT_PANEL_STYLE = {
  colorScheme: "light",
  textShadow: "none",
  "--background": "oklch(1 0 0)",
  "--foreground": "oklch(0.145 0 0)",
  "--card": "oklch(1 0 0)",
  "--card-foreground": "oklch(0.145 0 0)",
  "--muted": "oklch(0.97 0 0)",
  "--muted-foreground": "oklch(0.45 0 0)",
  "--border": "oklch(0.922 0 0)",
} as CSSProperties
